import { useDialog } from '@globals/dialog/useDialogStore'
import { ConfirmDialog } from '@shared/dialog/ConfirmDialog'
import { T } from '@transifex/react'
import { FC, useCallback, useEffect, useRef } from 'react'
import {
  useNavigationBlocker,
  useRegisterNavigationBlocker,
} from '../../routes/NavigationBlockerContext'

type PendingClose = { type: 'nav' | 'modal'; doClose?: () => void }

type DirtyFormBlockerProps = {
  isFormDirty: boolean
  isSubmitSuccessful: boolean
  isSubmitting: boolean
  onDiscardData?: () => void
  onKeepData?: () => Promise<void>
  dialogKey: string
  onRequestClose?: (attemptClose: (doClose: () => void) => boolean) => void
  /** When false, navigation is not blocked and the dialog will only open via the modal-close path (onRequestClose). Default true. */
  blockNavigation?: boolean
}

const DirtyFormBlocker: FC<DirtyFormBlockerProps> = ({
  isFormDirty,
  isSubmitSuccessful,
  isSubmitting,
  onDiscardData,
  onKeepData,
  dialogKey,
  onRequestClose,
  blockNavigation = true,
}) => {
  const { openDialog, closeDialog } = useDialog(dialogKey)
  const pendingCloseRef = useRef<PendingClose | null>(null)
  const isFormDirtyRef = useRef(isFormDirty)
  isFormDirtyRef.current = isFormDirty

  const handlerId = `dirty-form:${dialogKey}`
  const { proceed, reset, isActiveHandler, blocker } = useNavigationBlocker()
  // A submit handler that redirects on success does so while the form is still
  // dirty and before react-hook-form marks the submit successful, so an in-flight
  // submit must not be guarded — otherwise the guard blocks the redirect the save
  // just asked for and Save has to be pressed a second time.
  const shouldBlock = blockNavigation && isFormDirty && !isSubmitSuccessful && !isSubmitting
  useRegisterNavigationBlocker({
    id: handlerId,
    priority: 100,
    shouldBlock,
  })

  const isActive = isActiveHandler(handlerId)
  const isBlockedState = blocker.state === 'blocked' && isActive
  const isUnblockedState = blocker.state === 'unblocked'

  const attemptClose = useCallback(
    (doClose: () => void): boolean => {
      if (!isFormDirtyRef.current) {
        doClose()
        return true
      }
      pendingCloseRef.current = { type: 'modal', doClose }
      openDialog()
      return false
    },
    [openDialog],
  )

  useEffect(() => {
    if (isBlockedState) {
      // Save succeeded, submit started, or blockNavigation flipped off while the
      // leave prompt was already up. The router is still held; release it so the
      // navigation the user already asked for can finish without dismissing the dialog.
      if (!shouldBlock) {
        pendingCloseRef.current = null
        closeDialog()
        proceed()
        return
      }
      pendingCloseRef.current = { type: 'nav' }
      openDialog()
      return
    }
    if (isUnblockedState && pendingCloseRef.current?.type === 'nav') {
      pendingCloseRef.current = null
      closeDialog()
    }
  }, [closeDialog, isBlockedState, isUnblockedState, openDialog, proceed, shouldBlock])

  useEffect(() => {
    onRequestClose?.(attemptClose)
  }, [onRequestClose, attemptClose])

  const handleClose = () => {
    if (isBlockedState) {
      reset()
    }
    pendingCloseRef.current = null
  }

  const handleKeepData = () => {
    !!onKeepData && onKeepData()
    handleClose()
    closeDialog()
  }

  const handleDiscard = () => {
    !!onDiscardData && onDiscardData()
    closeDialog()
    const pending = pendingCloseRef.current
    pendingCloseRef.current = null
    if (pending?.type === 'modal') {
      pending.doClose?.()
    } else if (isBlockedState) {
      proceed()
    }
  }

  return (
    <ConfirmDialog
      dialogKey={dialogKey}
      title={<T _str="Are you sure you want to leave?" />}
      onClose={handleClose}
      onConfirm={handleKeepData}
      onCancel={handleDiscard}
      cancelButtonColor="error"
      cancelButtonText={<T _str="Leave" />}
      confirmButtonText={<T _str="Cancel" />}>
      <span>
        <T _str="Changes you've made won't be saved." />
      </span>
    </ConfirmDialog>
  )
}

export default DirtyFormBlocker
